import moment from "moment";
import "moment/locale/es";
import { dineroDisabledMask } from "../Components/Items";
import xmlToJson from "../Components/xmlToJson";
moment.locale("es");

//Gemelos: erp/src/Pdf/cancelarNotaDeCreditoVentaPdf.js (navegador) y
//erp_backend/Pdf/cancelarNotaDeCreditoVentaMailPdf.js (correo). El cuerpo es identico, solo cambian
//los imports y el export. Al tocar uno hay que copiar el cuerpo al otro.

//Mismo criterio que Utils/nombreEntidad.utils.js: el discriminante es la bandera "persona",
//no la presencia de razonSocial, porque los dos campos pueden venir llenos a la vez.
const nombreReceptor = (entidad) => {
    if (!entidad) return "N/A";

    const razonSocial = (entidad.razonSocial || "").trim();
    const nombreFisica = [entidad.nombre, entidad.apellidoPaterno, entidad.apellidoMaterno]
        .filter(Boolean)
        .join(" ")
        .trim();

    const nombrePrincipal = entidad.persona
        ? (razonSocial || nombreFisica)
        : (nombreFisica || razonSocial);

    return nombrePrincipal || "N/A";
};

//Catálogo de motivos de cancelación del SAT, el mismo que ofrece el modal de cancelación.
//Un código de dos dígitos que no esté en el catálogo se imprime tal cual, pero el campo también
//guarda notas internas ("Cancelación detectada por consulta de estatus SAT", que escriben los
//checarEstatusFiscal*) y ésas no son un motivo fiscal: no se imprimen.
const textoMotivoCancelacion = (motivo) => {
    switch (motivo) {
        case "01":
            return "01 - Comprobante emitido con errores con relación.";
        case "02":
            return "02 - Comprobante emitido con errores sin relación.";
        case "03":
            return "03 - No se llevó a cabo la operación.";
        case "04":
            return "04 - Operación nominativa relacionada en una factura global.";
        default:
            return /^\d{2}$/.test(motivo) ? motivo : "";
    }
};

//El acuse sólo enseña el motivo y el folio de sustitución cuando los hay: una nota sin CFDI o una
//cancelación detectada por consulta de estatus no traen ninguno de los dos.
const renglonesMotivoCancelacion = (cancelacion) => {
    const renglones = [];
    const motivo = textoMotivoCancelacion(cancelacion?.motivo);

    if (motivo) {
        renglones.push([
            { text: "Motivo de la cancelación:", style: "subheader" },
            { text: motivo, style: "textoChico" },
        ]);
    }

    if (cancelacion?.folioSustitucion) {
        renglones.push([
            { text: "Folio de sustitución:", style: "subheader" },
            { text: cancelacion.folioSustitucion, style: "textoChico" },
        ]);
    }

    return renglones;
};

//El acuse sólo lo devuelve el PAC al pedir la cancelación. Si la cancelación se hizo en el portal
//del SAT y el sistema apenas la detectó por consulta de estatus, no hay acuse que imprimir.
const leerAcuse = (acuseXml) => {
    if (!acuseXml || typeof acuseXml !== "string") {
        return { disponible: false };
    }

    try {
        const xmlAcuse = new xmlToJson().parseFromString(acuseXml);

        const folios = xmlAcuse?.children?.find((valueXml) => valueXml.name === "Folios");
        const signature = xmlAcuse?.children?.find((valueXml) => valueXml.name === "Signature");

        return {
            disponible: true,
            fecha: xmlAcuse?.attributes?.Fecha,
            uuid: folios?.children?.find((valueXml) => valueXml.name === "UUID")?.value,
            estatusUUID: folios?.children?.find((valueXml) => valueXml.name === "EstatusUUID")?.value,
            selloSAT: signature?.children?.find((valueXml) => valueXml.name === "SignatureValue")?.value,
        };
    } catch (error) {
        console.log("No se pudo parsear el acuse XML:", error.message);
        return { disponible: false };
    }
};

const LEYENDA_SIN_ACUSE = "Sin acuse del SAT — la cancelación se registró por consulta de estatus.";

//Con CFDI el folio es el de la factura; sin CFDI, el de la remisión.
const folioNota = (nota) => {
    const serie = nota.factura ? nota.facturaSerie : nota.remisionSerie;
    const numero = nota.factura ? nota.facturaNumero : nota.remisionNumero;

    if (numero === undefined || numero === null) return nota.serieNumero || "N/A";

    return [serie, numero].filter((valor) => valor !== undefined && valor !== null && valor !== "").join(" - ");
};

const cancelarNotaDeCreditoVentaPdf = (data) => {
    const empresa = data.empresa || {};
    const logoEmpresa = empresa.logo?.base64Completa || "";
    const logoBiwo = data.logoBiwo?.base64Completa || "";
    const dato = data.data || {};
    const cliente = data.cliente || {};

    //Una nota de crédito que sale de una factura global se timbró a PUBLICO EN GENERAL, no al cliente.
    const receptor = data.publicoEnGeneral
        ? { clave: cliente.clave, rfc: "XAXX010101000", nombre: "PUBLICO EN GENERAL" }
        : { clave: cliente.clave, rfc: cliente.rfc, nombre: nombreReceptor(cliente) };

    const esCfdi = dato.factura === true;
    const acuse = esCfdi ? leerAcuse(dato?.cancelacion?.acuse) : { disponible: false };
    const folio = folioNota(dato);

    const renglonesIzquierda = [
        [
            { text: "Folio:", style: "subheader" },
            { text: folio, style: "textoChico" },
        ],
        [
            { text: "Clave del Receptor:", style: "subheader" },
            { text: receptor.clave || "N/A", style: "textoChico" },
        ],
        [
            { text: "RFC del Receptor:", style: "subheader" },
            { text: receptor.rfc || "N/A", style: "textoChico" },
        ],
    ];

    if (esCfdi) {
        renglonesIzquierda.push([
            { text: "Fecha y hora de solicitud de la cancelación:", style: "subheader" },
            { text: acuse.fecha || "N/A", style: "textoChico" },
        ]);
    }

    //Con CFDI la tabla enseña el folio fiscal y el estatus que devolvió el SAT; sin CFDI no hay
    //nada que consultar en el SAT y el estado es el del sistema.
    const tablaDocumento = esCfdi
        ? [
            [
                { text: "Folio Fiscal (UUID)", style: "tituloTabla" },
                { text: "Estado CFDI", style: "tituloTabla" },
                { text: "Importe", style: "tituloTabla" },
            ],
            [
                { text: acuse.uuid || dato?.dataFactura?.uuid || "N/A", style: "textoTabla", alignment: "center" },
                { text: acuse.estatusUUID || "N/A", style: "textoTabla", alignment: "center" },
                { text: dineroDisabledMask(dato.totalPagar || 0), style: "textoTabla", alignment: "center" },
            ],
        ]
        : [
            [
                { text: "Folio", style: "tituloTabla" },
                { text: "Estado", style: "tituloTabla" },
                { text: "Importe", style: "tituloTabla" },
            ],
            [
                { text: folio, style: "textoTabla", alignment: "center" },
                { text: "Cancelado", style: "textoTabla", alignment: "center" },
                { text: dineroDisabledMask(dato.totalPagar || 0), style: "textoTabla", alignment: "center" },
            ],
        ];

    const content = [
        {
            style: "tableExample",
            table: {
                widths: ["18%", "52%", "30%"],
                layout: { defaultBorder: false },
                body: [
                    [
                        {
                            border: [false, false, false, false],
                            image: "logo",
                            fit: [80, 80],
                        },
                        {
                            border: [false, false, false, false],
                            table: {
                                headerRows: 1,
                                widths: ["100%"],
                                body: [
                                    [{ text: empresa.nombreComercial ? empresa.nombreComercial : empresa.nombre || "Nombre Empresa", style: "tableHeader" }],
                                    [
                                        {
                                            text: `R.F.C: ${empresa.rfc || "N/A"}`,
                                            style: "textoChico",
                                            alignment: "center",
                                        },
                                    ],
                                    [
                                        {
                                            text: empresa.calle ? `${empresa.calle} ${empresa.numeroCalle || ""}` : "Dirección N/A",
                                            style: "textoChico",
                                            alignment: "center",
                                        },
                                    ],
                                    [
                                        {
                                            text: empresa.colonia?.nombre
                                                ? `${empresa.colonia.nombre} C.P. ${empresa.colonia.cp || "N/A"}`
                                                : "Colonia N/A",
                                            style: "textoChico",
                                            alignment: "center",
                                        },
                                    ],
                                    [{ text: esCfdi ? "ACUSE DE CANCELACIÓN DE CFDI" : "ACUSE DE CANCELACIÓN DE NOTA DE CRÉDITO", style: "tableHeader" }],
                                ],
                            },
                            layout: "noBorders",
                        },
                        {
                            border: [false, false, false, false],
                            table: {
                                headerRows: 1,
                                widths: ["100%"],
                                body: [
                                    [
                                        {
                                            text: "Fecha:",
                                            style: "subheader",
                                            alignment: "center",
                                        },
                                    ],
                                    [
                                        {
                                            text: dato?.cancelacion?.fechaCancelacion ? moment(dato.cancelacion.fechaCancelacion).format("DD [de] MMMM [de] YYYY") : " - ",
                                            style: "textoChico",
                                            alignment: "center",
                                        },
                                    ],
                                ],
                            },
                            layout: "noBorders",
                        },
                    ],
                ],
            },
        },
        {
            style: "tableExample",
            table: {
                widths: ["50%", "50%"],
                layout: { defaultBorder: false },
                body: [
                    [
                        {
                            table: {
                                widths: ["50%", "50%"],
                                layout: { defaultBorder: false },
                                body: renglonesIzquierda,
                            },
                            layout: "noBorders",
                        },
                        {
                            table: {
                                widths: ["50%", "50%"],
                                layout: { defaultBorder: false },
                                body: [
                                    [
                                        { text: "Tipo de documento:", style: "subheader" },
                                        { text: esCfdi ? "Nota de crédito - Egreso" : "Nota de crédito", style: "textoChico" },
                                    ],
                                    [
                                        { text: "Nombre del Receptor:", style: "subheader" },
                                        { text: receptor.nombre || "N/A", style: "textoChico" },
                                    ],
                                    ...renglonesMotivoCancelacion(dato?.cancelacion),
                                ],
                            },
                            layout: "noBorders",
                        },
                    ],
                ],
            },
        },
        {
            style: "tableExample",
            table: {
                widths: ["60%", "20%", "20%"],
                layout: { defaultBorder: true },
                body: tablaDocumento,
            },
        },
    ];

    if (esCfdi) {
        content.push(
            [
                { text: "Sello digital del SAT:" }
            ],
            [
                { text: acuse.selloSAT || LEYENDA_SIN_ACUSE, style: "textoChico" }
            ],
        );
    }

    return {
        footer: { image: "logoBiwo", alignment: "center", width: 50 },
        content,
        images: {
            logo: logoEmpresa,
            logoBiwo: logoBiwo,
        },
        styles: {
            header: { fontSize: 9, bold: true },
            subheader: { fontSize: 8, bold: true },
            tableExample: { margin: [0, 5, 0, 5] },
            tableHeader: { bold: true, fontSize: 12, alignment: "center" },
            textoChico: { fontSize: 8 },
            tituloTabla: {
                fontSize: 8,
                bold: true,
                fillColor: "#cccccc",
                alignment: "center",
            },
            textoTabla: { fontSize: 8 },
        },
        pageSize: "LETTER",
        pageMargins: [20, 20, 20, 20],
    };
};

export default cancelarNotaDeCreditoVentaPdf;
